import { CloseOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { UserIcon } from 'assets/images/svg';
import { formatDisplayName } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectDependantPeopleByPersonID, selectPeople } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { Button, Col, Divider, Empty, Flex, Modal, Row, Select, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ManageAssociatedPeopleModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, personID }) => {
	const dispatch = useDispatch();
	const canManageAssociatedPeople = useManagePermittedRoles('manageAssociatedPeople');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		paddingLG,
		paddingXS,
		paddingSM,
		fontSizeSM,
		colorTextDescription,
		paddingXL,
		fontWeightStrong,
		colorIcon,
		paddingXXS
	} = theme.useToken().token;

	const people = useSelector(selectPeople);
	const associatedPeople = useSelector((state) => selectDependantPeopleByPersonID(state, personID));
	const { loadingUpdateProfile } = useSelector((state) => state.People);

	const [newAssociatedPeople, setNewAssociatedPeople] = useState([]);
	const [removedAssociatedPeople, setRemovedAssociatedPeople] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [internalVisible, setInternalVisible] = useState(false);

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const availablePeople = useMemo(() => {
		const existing = associatedPeople.map((p) => p.personID) || [];
		const filtered = existing.filter((id) => !removedAssociatedPeople.includes(id));
		const excluded = new Set([...filtered, ...newAssociatedPeople, personID]); // Exclude the main person

		return people
			.filter((p) => {
				const shouldExclude = excluded.has(p.personID);
				return !shouldExclude;
			})
			.map((p) => ({
				label: formatDisplayName(p)?.displayName || `${p.firstName} ${p.lastName}`,
				value: p.personID
			}));
	}, [people, associatedPeople, newAssociatedPeople, removedAssociatedPeople, personID]);

	// ✅ Handle cancel and reset changes
	const handleCancel = () => {
		setNewAssociatedPeople([]);
		setRemovedAssociatedPeople([]);
		setHasChanges(false);
		setVisible(false);
	};

	// ✅ Add person
	const handleAddPerson = (personIDToAdd) => {
		if (removedAssociatedPeople.includes(personIDToAdd)) {
			const updatedRemovedPeople = removedAssociatedPeople.filter((id) => id !== personIDToAdd);
			setRemovedAssociatedPeople(updatedRemovedPeople);
			setHasChanges(newAssociatedPeople.length > 0 || updatedRemovedPeople.length > 0);
		} else {
			const updatedNewPeople = [...newAssociatedPeople, personIDToAdd];
			setNewAssociatedPeople(updatedNewPeople);
			setHasChanges(updatedNewPeople.length > 0 || removedAssociatedPeople.length > 0);
		}
	};

	// ✅ Remove person
	const handleRemovePerson = (personIDToRemove) => {
		if (newAssociatedPeople.includes(personIDToRemove)) {
			const updatedNewPeople = newAssociatedPeople.filter((id) => id !== personIDToRemove);
			setNewAssociatedPeople(updatedNewPeople);
			setHasChanges(updatedNewPeople.length > 0 || removedAssociatedPeople.length > 0);
		} else {
			const updatedRemovedPeople = [...removedAssociatedPeople, personIDToRemove];
			setRemovedAssociatedPeople(updatedRemovedPeople);
			setHasChanges(newAssociatedPeople.length > 0 || updatedRemovedPeople.length > 0);
		}
	};

	// ✅ Submit changes
	const handleSubmit = () => {
		const peopleToAdd = newAssociatedPeople.filter((id) => !removedAssociatedPeople.includes(id));
		const peopleToRemove = removedAssociatedPeople.filter((id) => !newAssociatedPeople.includes(id));

		// Add new associated people
		if (peopleToAdd.length > 0) {
			peopleToAdd.forEach((personIDToAdd) => {
				dispatch(
					peopleActions.updatePerson({
						personID: personIDToAdd,
						data: {
							masterUserID: personID
						}
					})
				);
			});
		}

		// Remove associated people
		if (peopleToRemove.length > 0) {
			peopleToRemove.forEach((personIDToRemove) => {
				dispatch(
					peopleActions.updatePerson({
						personID: personIDToRemove,
						data: {
							masterUserID: null
						}
					})
				);
			});
		}

		handleCancel();
	};

	return (
		<>
			<Tooltip
				title={!canManageAssociatedPeople ? permissionDeniedMessage : 'Manage Associated People'}
				placement='top'
			>
				<span onClick={(e) => canManageAssociatedPeople && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<UsergroupAddOutlined color='currentColor' />}
							disabled={!canManageAssociatedPeople}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closable
				centered
				destroyOnClose
				width={763}
				footer={[
					<>
						<Divider />

						<Flex align='center' justify='flex-end' gap={paddingSM} key='footer'>
							<Button onClick={handleCancel} type='default' disabled={loadingUpdateProfile}>
								Cancel
							</Button>
							<Button
								onClick={handleSubmit}
								type='primary'
								loading={loadingUpdateProfile}
								disabled={!hasChanges}
							>
								Save Changes
							</Button>
						</Flex>
					</>
				]}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Manage Associated People
						</Title>
						<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
							Adding an associated person automatically grants them the same access permissions as the
							regular user.
						</Text>
					</Col>

					{/* Select new associated users */}
					<Col span={24}>
						<Flex vertical gap={paddingXS}>
							<Text strong>Add People</Text>
							<Select
								showSearch
								placeholder='Select People'
								style={{ width: '100%' }}
								options={availablePeople}
								value={null}
								onSelect={handleAddPerson}
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
							/>
						</Flex>
					</Col>

					{/* Show existing associated people */}
					<Col span={24}>
						<Flex vertical gap={paddingSM}>
							<Text strong>Exisiting Members</Text>
							{associatedPeople.filter((p) => !removedAssociatedPeople.includes(p.personID)).length ===
								0 && newAssociatedPeople.length === 0 ? (
								<Flex
									justify='center'
									align='center'
									style={{
										width: '100%',
										height: 85
									}}
								>
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description='No associated people yet. Add people using the dropdown above.'
									/>
								</Flex>
							) : (
								<Flex
									wrap='wrap'
									gap={paddingSM}
									align='flex-start'
									style={{
										height: 85,
										overflowY: 'auto',
										alignContent: 'flex-start',
										alignItems: 'flex-start'
									}}
								>
									{' '}
									{associatedPeople
										.filter((person) => !removedAssociatedPeople.includes(person.personID))
										.map((person) => {
											return (
												<Tag
													key={`existing-${person.personID}`}
													color='purple'
													style={{
														fontWeight: fontWeightStrong,
														borderColor: 'currentColor',
														marginInlineEnd: 0
													}}
												>
													<Flex align='center' gap={paddingXXS}>
														<UserIcon color='currentColor' />
														{formatDisplayName(person)?.displayName ||
															`${person.firstName} ${person.lastName}`}
														<CloseOutlined
															style={{ cursor: 'pointer', color: colorIcon }}
															onClick={() => handleRemovePerson(person.personID)}
														/>
													</Flex>
												</Tag>
											);
										})}
									{/* Newly added associated people */}
									{newAssociatedPeople.map((personID) => {
										const person = people.find((p) => p.personID === personID);
										return (
											<Tag
												key={`new-${personID}`}
												color='purple'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													opacity: 0.7,
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													<UserIcon color='currentColor' />
													{formatDisplayName(person)?.displayName ||
														`${person.firstName} ${person.lastName}`}{' '}
													(New)
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemovePerson(personID)}
													/>
												</Flex>
											</Tag>
										);
									})}
								</Flex>
							)}
						</Flex>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default ManageAssociatedPeopleModal;
